import { useState } from 'react';
import { Check, Loader2, X, AlertCircle } from 'lucide-react';

import { apiFetch, ApiError } from '../../lib/api';

/**
 * Channel B confirm card (#613, DEC-MRDN604Y Option A).
 *
 * Renders a phone-Fen `propose_rowan_write` tool call as a tap-to-approve card,
 * both live (from the stream) and from history (persisted metadata.toolCalls).
 * The write does NOT happen until Mark taps Approve → POST /api/rowan/approve,
 * which performs it through prime-gateway with the mcp:write key. `approve` is a
 * genuine human action, never something Fen self-executes.
 */

interface ProposalInput {
  action?: string;
  title?: string;
  description?: string;
  project_id?: string;
  priority?: string;
  task_id?: string;
  status?: string;
  context?: string;
  decision?: string;
  rationale?: string;
  category?: string;
  reason?: string;
}

interface ProposeCardProps {
  proposalId?: string;
  sessionId: string;
  input: unknown;
  /** Already approved (from session-GET approvedProposalIds) → render as done. */
  approved?: boolean;
  onApproved?: (id: string) => void;
}

type CardState = 'idle' | 'submitting' | 'done' | 'dismissed' | 'error';

const ACTION_LABELS: Record<string, string> = {
  create_task: 'Create task',
  update_task_status: 'Update task status',
  log_decision: 'Log decision',
};

export function ProposeCard({
  proposalId,
  sessionId,
  input,
  approved,
  onApproved,
}: ProposeCardProps) {
  const p = (input ?? {}) as ProposalInput;
  const [state, setState] = useState<CardState>(approved ? 'done' : 'idle');
  const [errorMsg, setErrorMsg] = useState('');

  const actionLabel = ACTION_LABELS[p.action ?? ''] ?? p.action ?? 'Rowan write';

  async function handleApprove() {
    if (!proposalId || !sessionId) {
      setErrorMsg('Missing proposal reference');
      setState('error');
      return;
    }
    setState('submitting');
    try {
      await apiFetch('/api/rowan/approve', {
        method: 'POST',
        body: { proposalId, sessionId },
      });
      setState('done');
      onApproved?.(proposalId);
    } catch (err) {
      // 409 = already approved (a prior or concurrent tap) — still "done".
      if (err instanceof ApiError && err.status === 409) {
        setState('done');
        onApproved?.(proposalId);
        return;
      }
      setErrorMsg(err instanceof ApiError ? err.message || err.code : 'Approval failed');
      setState('error');
    }
  }

  // Every field that gets WRITTEN must be shown. A card that renders less than
  // it sends asks Mark to approve something he cannot see — the same class as
  // the card that displayed "Project: understory" while filing the task
  // unfiled. Context and decision are log_decision's two required fields.
  const summaryRows: Array<[string, string | undefined]> = [
    ['Title', p.title],
    ['Task', p.task_id],
    ['Status', p.status],
    ['Priority', p.priority],
    ['Project', p.project_id],
    ['Context', p.context],
    ['Decision', p.decision],
    ['Rationale', p.rationale],
    ['Category', p.category],
  ];

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-gold/40 bg-gold/5">
      <div className="flex items-center gap-2 border-b border-gold/20 px-3 py-2">
        <span className="rounded bg-gold/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gold">
          Proposal
        </span>
        <span className="flex-1 truncate text-xs font-medium text-espresso">
          {actionLabel}
        </span>
      </div>

      <div className="space-y-1 px-3 py-2 text-xs text-espresso">
        {summaryRows
          .filter(([, v]) => v && String(v).trim().length > 0)
          .map(([label, v]) => (
            <div key={label} className="flex gap-2">
              <span className="w-16 shrink-0 text-bark/70">{label}</span>
              <span className="flex-1 break-words">{v}</span>
            </div>
          ))}
        {p.reason && (
          <div className="mt-1 border-t border-gold/10 pt-1 text-bark italic">
            {p.reason}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 px-3 pb-2">
        {state === 'done' && (
          <span className="flex items-center gap-1 text-xs font-medium text-green-700">
            <Check className="h-3.5 w-3.5" aria-hidden /> Approved
          </span>
        )}

        {state === 'dismissed' && (
          <span className="text-xs text-bark/60">Dismissed</span>
        )}

        {(state === 'idle' || state === 'error' || state === 'submitting') && (
          <>
            <button
              type="button"
              onClick={handleApprove}
              disabled={state === 'submitting'}
              className="flex items-center gap-1 rounded-full bg-gold px-3 py-1 text-xs font-medium text-ivory transition-opacity disabled:opacity-50"
            >
              {state === 'submitting' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Check className="h-3.5 w-3.5" aria-hidden />
              )}
              Approve
            </button>
            <button
              type="button"
              onClick={() => setState('dismissed')}
              disabled={state === 'submitting'}
              className="flex items-center gap-1 rounded-full border border-fog px-3 py-1 text-xs text-bark transition-colors hover:bg-fog/40 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" aria-hidden /> Dismiss
            </button>
          </>
        )}
      </div>

      {state === 'error' && (
        <div className="flex items-start gap-1 border-t border-red-100 bg-red-50 px-3 py-1.5 text-[11px] text-red-700">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>{errorMsg}</span>
        </div>
      )}
    </div>
  );
}
